import Vue from 'vue';
import { getDeviceId, sendAppMetricaEvent } from '../../js/utils/service-helpers.js';

export default {
    namespaced: true,

    state: {
        defaultValue: {
            MAIN_PAGE_ASSISTANT_LINK: '/entity/assist/drugoe-poruchenie/product/6655',
            LOGIN_AUTH_CHANNELS_CONFIG: {
                telegram: true,
                flashcall: true,
                sms: true
            }
        },
        config: {
            MAIN_PAGE_ASSISTANT_LINK: '/entity/assist/drugoe-poruchenie-novoe',
            MAIN_PAGE_ASSISTANT_TEXT: '',
            ENTITY_DELIVERY_ADDRESS_SHOW: true,

            TOWER_ADDRESS_POPUP: true,
            TOWER_ENTITIES_SORT: true,

            SHOW_MIN_PRICE: false,

            SHOW_SERVICES_IN_TWO_ROWS: true,
            LOGIN_AUTH_CHANNELS_CONFIG: {
                telegram: true,
                flashcall: true,
                sms: true
            },

            
        },
        abTests: {},
        abTestAssignments: {},
        abTestsLoaded: false,
        abTestsFetching: false,
        abTestsReported: {},
        abTestsSourceKey: null
    },
    mutations: {
        SET_CONFIG(state, payload) {
            state.config = {
                ...state.config,
                ...payload
            };
        },
        SET_AB_TESTS(state, abTests) {
            state.abTests = abTests;

            // Применяем конфиг только для новых тестов (первичная инициализация)
            Object.keys(abTests).forEach(testKey => {
                const test = abTests[testKey];
                const hasAssignment = Object.prototype.hasOwnProperty.call(state.abTestAssignments, testKey);
                if (!hasAssignment && test.config && typeof test.config === 'object') {
                    state.config = {
                        ...state.config,
                        ...test.config
                    };
                }
            });
            state.abTestsLoaded = true;
        },
        SET_AB_TEST_ASSIGNMENT(state, { testKey, variantKey }) {
            // Фиксируем первое назначение и не переопределяем его повторно
            if (!Object.prototype.hasOwnProperty.call(state.abTestAssignments, testKey)) {
                state.abTestAssignments[testKey] = variantKey;
            }
        },
        SET_AB_TESTS_FETCHING(state, fetching) {
            state.abTestsFetching = fetching;
        },
        SET_AB_TESTS_SOURCE_KEY(state, sourceKey) {
            state.abTestsSourceKey = sourceKey;
        },
        MARK_AB_TEST_REPORTED(state, testKey) {
            state.abTestsReported = {
                ...state.abTestsReported,
                [testKey]: true
            };
        }
    },
    actions: {
        async fetchAbTestConfig({ state, commit, rootState }) {
            try {
                const userId = rootState.auth?.user?.id;
                const deviceId = await getDeviceId();
                
                if (!deviceId && !userId) {     
                    return;
                }
                const sourceKey = `${userId || 'nouser'}|${deviceId || 'nodevice'}`;

                // Блокируем параллельные и повторные загрузки для одного и того же источника
                if (state.abTestsFetching) {
                    return;
                }
                if (state.abTestsLoaded && state.abTestsSourceKey === sourceKey) {
                    return;
                }
                commit('SET_AB_TESTS_FETCHING', true);
                
                const params = new URLSearchParams();
                if (userId) params.append('user_id', userId);
                if (deviceId) params.append('device_id', deviceId);
                
                const response = await fetch(`${process.env.VUE_APP_API_URL}/front/ab-tests/config?${params.toString()}`);
                
                if (response.ok && !response.bodyUsed) {
                    try {
                        const data = await response.json();
                        const abTests = data.tests || data || {};
                        commit('SET_AB_TESTS', abTests);
                        commit('SET_AB_TESTS_SOURCE_KEY', sourceKey);
                        
                        // Сохраняем назначения вариантов
                        Object.keys(abTests).forEach(testKey => {
                            const test = abTests[testKey];
                            commit('SET_AB_TEST_ASSIGNMENT', { 
                                testKey, 
                                variantKey: test.variant 
                            });
                        });

                        // Отправляем отдельное событие в AppMetrica для каждого A/B теста
                        for (const testKey of Object.keys(abTests)) {
                            const test = abTests[testKey];
                            if (!state.abTestsReported[testKey]) {
                                sendAppMetricaEvent('ab_test_assigned', {
                                    test_key: testKey,
                                    variant_key: test.variant,
                                    test_config: test.config || {},
                                    user_id: userId || null,
                                    device_id: deviceId || null
                                });
                                commit('MARK_AB_TEST_REPORTED', testKey);
                            }
                        }
                        
                        // Также отправляем общее событие с количеством тестов
                        if (Object.keys(abTests).length > 0) {
                            sendAppMetricaEvent('ab_tests_loaded', {
                                tests_count: Object.keys(abTests).length,
                                user_id: userId || null,
                                device_id: deviceId || null
                            });
                        }
                    } catch (jsonError) {
                        console.error('Ошибка парсинга JSON A/B тестов:', jsonError);
                    }
                } else {
                    console.warn('Response не OK или body уже использован:', {
                        ok: response.ok,
                        bodyUsed: response.bodyUsed,
                        status: response.status
                    });
                }
            } catch (error) {
                console.error('Ошибка загрузки A/B тестов:', error);
            } finally {
                commit('SET_AB_TESTS_FETCHING', false);
            }
        },
        
        async trackAbTestConversion({ state, rootState }, { testKey, eventData = {} }) {
            try {
                const userId = rootState.auth?.user?.id;
                const deviceId = await getDeviceId();
                
                if (!userId && !deviceId) {
                    return;
                }
                
                const variantKey = state.abTestAssignments[testKey];
                
                // Отправляем в API
                await fetch(`${process.env.VUE_APP_API_URL}/front/ab-tests/events`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        test_key: testKey,
                        variant_key: variantKey,
                        event_type: 'conversion',
                        user_id: userId,
                        device_id: deviceId,
                        event_data: eventData
                    })
                });
                
                // Отправляем событие в AppMetrica
                sendAppMetricaEvent('ab_test_conversion', {
                    test_key: testKey,
                    variant_key: variantKey,
                    user_id: userId || null,
                    device_id: deviceId || null,
                    event_data: eventData
                });
            } catch (error) {
                console.error('Ошибка трекинга A/B теста:', error);
            }
        },

        async updateAbTestsUserId({ state, rootState }) {
            try {
                const userId = rootState.auth?.user?.id;
                const deviceId = await getDeviceId();

                if (!userId || !deviceId) {
                    return;
                }

                const testKeys = Object.keys(state.abTests || {});
                if (!testKeys.length) {
                    return;
                }

                await Promise.all(
                    testKeys.map(testKey =>
                        fetch(`${process.env.VUE_APP_API_URL}/front/ab-tests/events/update-user-id`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                test_key: testKey,
                                device_id: deviceId,
                                user_id: userId
                            })
                        }).catch(err => {
                            console.error('Ошибка обновления user_id для A/B теста', testKey, err);
                        })
                    )
                );
            } catch (error) {
                console.error('Ошибка updateAbTestsUserId:', error);
            }
        }
    },
    getters: {
        getAbTestVariant: (state) => (testKey) => {
            return state.abTestAssignments[testKey] || null;
        },
        
        getAbTestConfig: (state) => (testKey) => {
            const test = state.abTests[testKey];
            return test && typeof test.config === 'object' ? test.config : {};
        },
        
        isInAbTest: (state) => (testKey) => {
            const assignments = JSON.parse(JSON.stringify(state.abTestAssignments));
            const hasKey = testKey in assignments;
            return hasKey;
        },
        
        getConfigValue: (state) => (key) => {
            return state.config[key] || state.defaultValue[key] || null;
        }
    }
}